import type { VectorEdge, VectorNetwork, VectorRegion } from "@/document/model/vectorNetwork";
import { sampleNetworkRegionContours, type CurveStepSource } from "@/document/operations/vectorGeometry";
import { contourParentsContour, pointInPolygon, type Point } from "./pathGeometry";

export interface VectorRegionGeometry {
  region: VectorRegion;
  edgeRefs: Array<{ edgeId: string; forward: boolean }>;
  edges: VectorEdge[];
  contour: Point[];
  holes: Point[][];
}

export function vectorRegionGeometries(network: VectorNetwork, steps: CurveStepSource = 24): VectorRegionGeometry[] {
  const byKey = new Map(network.regions.map(region => [regionKey(region.edgeIds), region]));
  const edgesById = new Map(network.edges.map(edge => [edge.id, edge]));
  const geometries = sampleNetworkRegionContours(network, steps).map(region => ({
    region: byKey.get(regionKey(region.edgeIds)) ?? { id: regionKey(region.edgeIds), edgeIds: region.edgeIds },
    edgeRefs: region.edgeRefs,
    edges: region.edgeIds.flatMap(edgeId => edgesById.get(edgeId) ?? []),
    contour: region.points,
    holes: [],
  }));

  const childIndexesByParent = directChildRegionIndexes(geometries);
  return geometries.map((geometry, index) => ({
    ...geometry,
    holes: (childIndexesByParent.get(index) ?? []).map(childIndex => geometries[childIndex]!.contour),
  }));
}

export function filledVectorRegionContours(network: VectorNetwork, steps: CurveStepSource = 24): Point[][] {
  return filledVectorRegionContourGroups(network, steps).flat();
}

export function filledVectorRegionContourGroups(network: VectorNetwork, steps: CurveStepSource = 24): Point[][][] {
  return vectorRegionGeometries(network, steps)
    .filter(geometry => geometry.region.filled !== false)
    .map(geometry => [geometry.contour, ...geometry.holes]);
}

export function vectorRegionContourGroups(network: VectorNetwork, steps: CurveStepSource = 24): Point[][][] {
  const geometries = vectorRegionGeometries(network, steps);
  const childIndexesByParent = directChildRegionIndexes(geometries);
  return geometries
    .filter((_, index) => regionDepth(index, childIndexesByParent) % 2 === 0)
    .map(geometry => [geometry.contour, ...geometry.holes]);
}

export function promoteVectorRegions(network: VectorNetwork, defaultFilled = false): VectorRegion[] {
  return vectorRegionGeometries(network).map(geometry => ({
    id: geometry.region.id,
    edgeIds: [...geometry.region.edgeIds],
    fillStyleId: geometry.region.fillStyleId,
    filled: geometry.region.filled ?? defaultFilled,
  }));
}

export function hitTestVectorRegion(network: VectorNetwork, point: Point): VectorRegionGeometry | undefined {
  return vectorRegionGeometries(network)
    .filter(geometry => pointInPolygon(point, geometry.contour))
    .sort((left, right) => Math.abs(signedArea(left.contour)) - Math.abs(signedArea(right.contour)))[0];
}

export function regionKey(edgeIds: string[]): string {
  return edgeIds.join(":");
}

function directChildRegionIndexes(geometries: VectorRegionGeometry[]): Map<number, number[]> {
  const childIndexesByParent = new Map<number, number[]>();

  // Precompute areas and bounding boxes once. The nesting search is O(n^2) over
  // regions and `contourParentsContour` runs segment-intersection tests, so a
  // cheap bounding-box rejection before it keeps large networks responsive.
  const areas = geometries.map(geometry => Math.abs(signedArea(geometry.contour)));
  const bounds = geometries.map(geometry => contourBounds(geometry.contour));

  for (let childIndex = 0; childIndex < geometries.length; childIndex += 1) {
    const child = geometries[childIndex]!;
    const childArea = areas[childIndex]!;
    const childBounds = bounds[childIndex]!;
    let parentIndex: number | undefined;
    let parentArea = Number.POSITIVE_INFINITY;

    for (let candidateIndex = 0; candidateIndex < geometries.length; candidateIndex += 1) {
      if (candidateIndex === childIndex) {
        continue;
      }

      const candidateArea = areas[candidateIndex]!;
      if (candidateArea <= childArea || candidateArea >= parentArea) {
        continue;
      }

      if (!boundsContains(bounds[candidateIndex]!, childBounds)) {
        continue;
      }

      const candidate = geometries[candidateIndex]!;
      if (!contourParentsContour(candidate.contour, child.contour)) {
        continue;
      }

      parentIndex = candidateIndex;
      parentArea = candidateArea;
    }

    if (parentIndex !== undefined) {
      childIndexesByParent.set(parentIndex, [...(childIndexesByParent.get(parentIndex) ?? []), childIndex]);
    }
  }

  return childIndexesByParent;
}

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function contourBounds(points: Point[]): Bounds {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  return { minX, minY, maxX, maxY };
}

function boundsContains(outer: Bounds, inner: Bounds): boolean {
  return outer.minX <= inner.minX && outer.minY <= inner.minY && outer.maxX >= inner.maxX && outer.maxY >= inner.maxY;
}

function regionDepth(index: number, childIndexesByParent: Map<number, number[]>): number {
  let depth = 0;
  let current = index;
  while (true) {
    const parent = Array.from(childIndexesByParent.entries()).find(([, childIndexes]) => childIndexes.includes(current))?.[0];
    if (parent === undefined) {
      return depth;
    }

    depth += 1;
    current = parent;
  }
}

function signedArea(points: Point[]): number {
  return points.reduce((area, point, index) => {
    const next = points[(index + 1) % points.length]!;
    return area + point.x * next.y - next.x * point.y;
  }, 0) / 2;
}
